import { FC, ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { InViewport } from 'dibs-in-viewport/exports/InViewport';
import { Link } from 'dibs-elements/exports/Link';

import { Carousel } from 'dibs-carousel';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { trackEvent } from 'dibs-tracking';
import { getSrcsetUrl } from 'dibs-image-utils/exports/srcSet';
import { SharedCarouselDot } from '../../shared/SharedCarouselDot/SharedCarouselDot';
import { SbSharedContentModuleBannerItem } from './SbSharedContentModuleBannerItem';

import { SbSharedContentModuleBannerTiles_browseContentModule$key } from './__generated__/SbSharedContentModuleBannerTiles_browseContentModule.graphql';

import styles from './SbSharedContentModuleBannerTiles.scss';

function fireTracking(): void {
    trackEvent({
        category: 'ecommerce',
        action: 'promotion click',
        label: 'collections banner tiles',
    });
}

const BannerWrapper: FC<{ href: string }> = ({ href, children }) => {
    if (href) {
        return (
            <Link underline="none" href={href}>
                {children}
            </Link>
        );
    }
    return <>{children}</>;
};

type Props = {
    browseContentModule: SbSharedContentModuleBannerTiles_browseContentModule$key;
    renderImage?: ({
        src,
        alt,
        className,
        srcSet,
        sizes,
    }: {
        src: string;
        alt: string;
        className: string;
        srcSet: string;
        sizes?: string;
    }) => ReactNode;
};

export const SbSharedContentModuleBannerTiles: FC<Props> = ({
    browseContentModule: browseContentModuleRef,
    renderImage = ({ src, alt, className, srcSet, sizes }) => (
        <img src={src} alt={alt} className={className} srcSet={srcSet} sizes={sizes} />
    ),
}) => {
    const browseContentModule = useFragment(
        graphql`
            fragment SbSharedContentModuleBannerTiles_browseContentModule on ContentModuleBannerTiles {
                termsLink
                banners {
                    ...SbSharedContentModuleBannerItem_banner
                    ctaUrl
                }
                tiles {
                    imageUrl
                    title
                    linkData {
                        ...SeoLink_linkData
                    }
                }
            }
        `,
        browseContentModuleRef
    );

    const { tiles, termsLink, banners } = browseContentModule || {};
    const bannersLength = banners?.length || 0;

    if (!bannersLength) {
        return null;
    }

    return (
        <div className={styles.wrapper}>
            <InViewport>
                {({ inViewport }) => (
                    <Carousel
                        isAutoRun={inViewport && bannersLength > 1}
                        classNames={{ wrapper: styles.carousel }}
                        dataTn="hero-items-carousel"
                        showDots={bannersLength > 1}
                        hideArrows
                        totalItems={bannersLength}
                        itemsPerPage={1}
                        step={1}
                        renderItem={({ index }: { index: number }) =>
                            banners?.[index] ? (
                                <BannerWrapper href={banners[index]?.ctaUrl || ''}>
                                    <SbSharedContentModuleBannerItem
                                        banner={banners[index]}
                                        renderImage={renderImage}
                                        termsLink={termsLink || ''}
                                    />
                                </BannerWrapper>
                            ) : null
                        }
                        renderDot={({ isCurrentDot }: { isCurrentDot?: boolean } = {}) => (
                            <SharedCarouselDot isActive={!!isCurrentDot} />
                        )}
                    />
                )}
            </InViewport>
            <div className={styles.tilesWrapper}>
                {(tiles || []).map(
                    (tile, index) =>
                        tile &&
                        tile.title && (
                            <SeoLink
                                ariaLabel={tile.title}
                                dataTn={`browse-cms-tile-${index}`}
                                onClick={fireTracking}
                                className={styles.tile}
                                key={`browse-cms-tile-${index}`}
                                linkData={tile?.linkData}
                            >
                                {renderImage({
                                    src: tile.imageUrl || '',
                                    alt: tile.title,
                                    className: styles.tileImage,
                                    srcSet: `
                                        ${getSrcsetUrl(tile.imageUrl || '', 72)} 1x,
                                        ${getSrcsetUrl(tile.imageUrl || '', 144)} 2x,
                                    `,
                                })}
                                <span className={styles.tileTitle}>{tile.title}</span>
                            </SeoLink>
                        )
                )}
            </div>
        </div>
    );
};
